import { NOYAU_ORDER } from '@/lib/noyaux';
import {
  toTahitiCreateBody,
  toTahitiTagCreateBody,
  toTahitiUpdateBody,
  toWord,
  toWordSummary,
  toWriteBaseline,
} from '@/lib/tahiti-adapter';
import type { TahitiCreateTemplate, WordCreate, WordPatch } from '@/lib/tahiti-adapter';
import { ApiError, tahitiRequest } from '@/lib/tahiti-client';
import { DEFAULT_WORD_SORT } from '@/lib/words-query';
import type { NoyauFilterValue, WordSort } from '@/lib/words-query';
import { tahitiTagSchema } from '@/schemas/tag';
import type { TahitiTag } from '@/schemas/tag';
import { tahitiWordSchema } from '@/schemas/tahiti';
import type { TahitiWord } from '@/schemas/tahiti';
import { tagCategorySchema } from '@/schemas/word';
import type { NoyauCode, TagCategory, Word, WordSummary, WordTag } from '@/schemas/word';
import { z } from 'zod';

// Re-export so existing imports `from '@/lib/api'` keep working after the
// transport was consolidated onto the Tahiti client.
export { ApiError } from '@/lib/tahiti-client';
export type { WordCreate, WordPatch } from '@/lib/tahiti-adapter';

/** Default page size for the paginated `/api/meta/list` reads. */
export const WORDS_PAGE_SIZE = 100;

/**
 * One page of search results. `items` are the lightweight summaries the sidebar
 * renders; `words` are the same rows mapped to full {@link Word}s (meta/list
 * projects the body + ponderation), so callers can prime the detail cache and
 * avoid a second round-trip. `nextPage` is null on the last page.
 */
export type WordPage = { items: WordSummary[]; words: Word[]; nextPage: number | null };

/**
 * The result of {@link wordsApi.searchUpTo}: up to `maxResults` matches for a
 * search+filter expression. `total` is the count actually fetched (NOT a true
 * server-side total — Tahiti's `count` field is unreliable, see the function's
 * docstring); `truncated` is true when more matches likely exist beyond `maxResults`.
 */
export type WordSearchBatch = {
  items: WordSummary[];
  words: Word[];
  total: number;
  truncated: boolean;
};

/**
 * Filter + sort parameters for {@link wordsApi.search}. `revisedSince` is
 * accepted for source compatibility but is NOT applied against live Tahiti
 * (no date-range column on `pms_word` here) — it degrades to a no-op.
 */
export type WordSearchParams = {
  noyaux?: NoyauFilterValue[];
  tags?: string[];
  revisedSince?: string | undefined;
  /** When true, only words flagged `to_check` are matched. */
  toCheckOnly?: boolean;
  sort?: WordSort;
  /** When true, the text query is matched exactly (`=query`) instead of by substring. */
  exact?: boolean;
};

// ── meta/list request building ────────────────────────────────────────────────
//
// devdb serves reads as `GET /api/meta/list?lang=fr&page=&pagesize=&basecode=
// pms_word&search=<expr>&request=<urlencoded JSON>`. The `search` expression IS
// the filter: `=x` matches exactly, a comma means OR, a space means AND, matched
// across `request.whereConditions` (title, noyau_sing, tags). `request` also
// carries `fields` and `orderBy` ([{field,direction}]).

const KEY_TO_CODE = new Map(NOYAU_ORDER.map((n) => [n.key, n.code]));

const FIELDS = [
  'guid',
  'id',
  'title',
  'noyau_sing',
  'noyau_validated',
  'tags',
  'content',
  'see_also',
  'ponderation_sgp',
  'ponderation_spg',
  'ponderation_psg',
  'ponderation_pgs',
  'ponderation_gps',
  'ponderation_gsp',
  'fonction',
  'genre',
  'to_check',
  'reasons',
  'created_at',
  'updated_at',
];

const WHERE_FIELDS = ['title', 'noyau_sing', 'tags', 'to_check'];

/**
 * Build the `search` expression from a text query + filters. Terms are
 * AND-joined (space); the noyau filter is an OR group of exact `=CODE` tokens;
 * the tag filter is an OR group of (substring) labels. The pseudo-value `none`
 * has no Tahiti column and is skipped. When `params.exact` is set, the text
 * term itself is matched exactly (`=query`) rather than by substring — used by
 * the relevance-mode "complete match" sub-fetch (see words-nav.tsx).
 */
function buildSearch(q: string | undefined, params: WordSearchParams | undefined): string {
  const terms: string[] = [];

  const text = q?.trim();
  if (text) terms.push(params?.exact ? `=${text}` : text);

  const noyauCodes = (params?.noyaux ?? [])
    .filter((v): v is Exclude<NoyauFilterValue, 'none'> => v !== 'none')
    .map((key) => KEY_TO_CODE.get(key))
    .filter((code): code is NoyauCode => code !== undefined);
  if (noyauCodes.length > 0) terms.push(noyauCodes.map((c) => `=${c}`).join(','));

  if (params?.tags?.length) terms.push(params.tags.join(','));

  // NOTE: unverified against live devdb (unlike the noyau/tag exact-match
  // terms above, which were confirmed working end-to-end) — `to_check` is a
  // boolean column returned as a string ("true"/"false"), and matching it via
  // the generic `search` expression's exact-term mechanism (`=true`) assumes
  // Tahiti's search matches against that column's string value the same way
  // it does for `noyau_sing`. If devdb rejects/ignores this, `toCheckOnly`
  // will need a different mechanism (fallback: client-side filter over a
  // fetched page, mirroring `revisedSince`'s documented no-op degradation).
  if (params?.toCheckOnly) terms.push('=true');

  return terms.join(' ');
}

type OrderByTerm = { field: string; direction: 'ASC' | 'DESC' };

const SORT_TO_ORDER_BY: Record<WordSort, OrderByTerm[]> = {
  // Relevance ranking happens client-side (search-relevance.ts) against a
  // dedicated fetch path — this entry is only reached as the `effectiveSort`
  // fallback when relevance mode doesn't apply (no active query), so it
  // mirrors the plain alphabetical default.
  relevance: [
    { field: 'title', direction: 'ASC' },
    { field: 'id', direction: 'ASC' },
  ],
  'title-asc': [
    { field: 'title', direction: 'ASC' },
    { field: 'id', direction: 'ASC' },
  ],
  'title-desc': [
    { field: 'title', direction: 'DESC' },
    { field: 'id', direction: 'ASC' },
  ],
  // No `revised` column on this deployment — degrade to update timestamp.
  'revised-desc': [
    { field: 'updated_at', direction: 'DESC' },
    { field: 'title', direction: 'ASC' },
  ],
  'revised-asc': [
    { field: 'updated_at', direction: 'ASC' },
    { field: 'title', direction: 'ASC' },
  ],
  'created-desc': [
    { field: 'created_at', direction: 'DESC' },
    { field: 'title', direction: 'ASC' },
  ],
  'created-asc': [
    { field: 'created_at', direction: 'ASC' },
    { field: 'title', direction: 'ASC' },
  ],
  noyau: [
    { field: 'noyau_sing', direction: 'ASC' },
    { field: 'title', direction: 'ASC' },
  ],
  'to-check': [
    { field: 'to_check', direction: 'DESC' },
    { field: 'title', direction: 'ASC' },
  ],
};

const tahitiWordListSchema = z.array(tahitiWordSchema);

// `GET /api/meta/object?mode=full` returns `{value0,value}`-wrapped fields (and
// may wrap the row in an array). We accept either shape loosely, then unwrap.
const objectRowSchema = z.record(z.string(), z.unknown());
const objectDataSchema = z.union([z.array(objectRowSchema), objectRowSchema]);

// Write responses nest `{ operation, affectedRows, data: [{id, guid}], ... }`
// under the envelope's `data`. We only need the new row's identifiers.
const writeResultSchema = z
  .object({
    data: z
      .array(
        z.object({ id: z.union([z.string(), z.number()]).optional(), guid: z.string().optional() }),
      )
      .optional(),
  })
  .passthrough();

/** The flat field keys we unwrap from a `meta/object` row. */
const OBJECT_FIELDS = [
  'id',
  'guid',
  'title',
  'noyau_sing',
  'noyau_validated',
  'tags',
  'content',
  'see_also',
  'ponderation_sgp',
  'ponderation_spg',
  'ponderation_psg',
  'ponderation_pgs',
  'ponderation_gps',
  'ponderation_gsp',
  'fonction',
  'genre',
  'to_check',
  'reasons',
  'created_at',
  'updated_at',
] as const;

/** Unwrap a `{value0,value}`-wrapped meta/object row into the flat {@link TahitiWord}. */
function unwrapObjectRow(row: Record<string, unknown>): TahitiWord {
  const pick = (key: string): string | undefined => {
    const field = row[key];
    if (field !== null && typeof field === 'object' && !Array.isArray(field)) {
      const wrapped = field as { value0?: unknown; value?: unknown };
      // value0 is the current stored value (what we diff against on write).
      const value = wrapped.value0 ?? wrapped.value;
      return value == null ? undefined : String(value);
    }
    return field == null ? undefined : String(field);
  };
  const out: Record<string, string | undefined> = {};
  for (const key of OBJECT_FIELDS) out[key] = pick(key);
  return tahitiWordSchema.parse(out);
}

/** GET a `meta/list` page and return the raw Tahiti items. */
async function metaList(opts: {
  search?: string | undefined;
  orderBy: OrderByTerm[];
  page: number;
  whereConditions?: string[];
}) {
  const request = {
    fields: FIELDS,
    orderBy: opts.orderBy,
    whereConditions: opts.whereConditions ?? WHERE_FIELDS,
  };
  const sp = new URLSearchParams({
    lang: 'fr',
    basecode: 'pms_word',
    page: String(opts.page),
    pagesize: String(WORDS_PAGE_SIZE),
    request: JSON.stringify(request),
  });
  if (opts.search?.trim()) sp.set('search', opts.search.trim());
  return tahitiRequest(`/meta/list?${sp.toString()}`, tahitiWordListSchema);
}

/**
 * Read a single full `pms_word` row by guid (flat, value0-resolved). `id`
 * (the numeric primary key) is included when the caller already has it —
 * verified against devdb, `meta/object` was probed with both `id` and `guid`
 * together — but is optional since most callers only start with the guid.
 */
async function fetchRow(guid: string, id?: string): Promise<TahitiWord> {
  const sp = new URLSearchParams({ obj_code: 'pms_word', guid, lang: 'fr', mode: 'full' });
  if (id) sp.set('id', id);
  const data = await tahitiRequest(`/meta/object?${sp.toString()}`, objectDataSchema);
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new ApiError(404, 'NOT_FOUND', `Word not found: ${guid}`);
  return unwrapObjectRow(row);
}

// devdb rejects a minimal insert body (500); a create must overlay its fields
// onto the FULL blank template (`GET /meta/object?obj_code=<code>`, no id/guid)
// and POST the whole record back. The template is the static schema shape for
// the object — its identity cells (id/guid/…) are all null and minted per
// insert, so it is safe to fetch once per obj_code and reuse across every
// create (incl. a bulk loop).
const createTemplatePromises = new Map<string, Promise<TahitiCreateTemplate>>();

function fetchCreateTemplate(objCode: string): Promise<TahitiCreateTemplate> {
  const cached = createTemplatePromises.get(objCode);
  if (cached) return cached;
  const sp = new URLSearchParams({ obj_code: objCode, lang: 'fr' });
  const promise = tahitiRequest(`/meta/object?${sp.toString()}`, objectDataSchema)
    .then((data) => {
      const row = Array.isArray(data) ? data[0] : data;
      if (!row)
        throw new ApiError(500, 'TEMPLATE_FAILED', `Create template unavailable: ${objCode}`);
      return row;
    })
    .catch((err: unknown) => {
      // Drop the cached rejection so a later create can retry the fetch.
      createTemplatePromises.delete(objCode);
      throw err;
    });
  createTemplatePromises.set(objCode, promise);
  return promise;
}

// ── Words API ─────────────────────────────────────────────────────────────────

export const wordsApi = {
  /**
   * Search words by title + filters, sorted. Returns one page of lightweight
   * summaries plus the next page number (null when the last page was reached).
   */
  search: async (q?: string, params?: WordSearchParams, page = 1): Promise<WordPage> => {
    const items = await metaList({
      search: buildSearch(q, params),
      orderBy: SORT_TO_ORDER_BY[params?.sort ?? DEFAULT_WORD_SORT],
      page,
    });
    return {
      items: items.map(toWordSummary),
      words: items.map(toWord),
      nextPage: items.length === WORDS_PAGE_SIZE ? page + 1 : null,
    };
  },

  /**
   * Fetch up to `maxResults` matches for a search+filter expression (title-asc
   * server order), paging `meta/list` until either a partial (non-full) page
   * is returned or `maxResults` is reached. Used by relevance mode to gather a
   * rankable set without relying on `search`'s single-page/`getNextPageParam`
   * contract.
   *
   * Deliberately does NOT use the envelope's `count` field to detect the true
   * total match count: verified against live devdb, `count` does not reflect
   * the pre-pagination total (it degrades to the current page's row count),
   * which made the cap-detection break after page 1 on real data. Instead,
   * `truncated` is inferred from "did the last fetched page come back full at
   * or past the cap" — a full page always means more rows *might* exist, so
   * treat it as truncated rather than risk silently dropping real matches.
   */
  searchUpTo: async (
    q: string | undefined,
    params: WordSearchParams | undefined,
    maxResults: number,
  ): Promise<WordSearchBatch> => {
    const search = buildSearch(q, params);
    const orderBy = SORT_TO_ORDER_BY['title-asc'];
    const items: TahitiWord[] = [];
    let page = 1;
    let lastPageFull = false;
    for (;;) {
      const batch = await metaList({ search, orderBy, page });
      items.push(...batch);
      lastPageFull = batch.length === WORDS_PAGE_SIZE;
      if (!lastPageFull || items.length >= maxResults) break;
      page += 1;
    }
    const truncated = lastPageFull && items.length >= maxResults;
    const capped = truncated ? items.slice(0, maxResults) : items;
    return {
      items: capped.map(toWordSummary),
      words: capped.map(toWord),
      total: capped.length,
      truncated,
    };
  },

  /** Fetch a single full word by its Tahiti `guid` (the `/words/$wordId` param). */
  get: async (guid: string): Promise<Word> => {
    return toWord(await fetchRow(guid));
  },

  /**
   * Resolve a batch of numeric ids (as referenced by internal `[text](#<id>)`
   * links / see_also entries) in a single request — exact match, `whereConditions`
   * overridden to `id` instead of the default title/noyau/tags. Ids have no
   * embedded whitespace, so the `=`-exact/comma-OR grammar batches them cleanly
   * (unlike a multi-word title, see the BUILD-368 devdb probe).
   */
  getByIds: async (ids: string[]): Promise<WordSummary[]> => {
    if (ids.length === 0) return [];
    const items = await metaList({
      search: ids.map((id) => `=${id}`).join(','),
      whereConditions: ['id'],
      orderBy: SORT_TO_ORDER_BY['title-asc'],
      page: 1,
    });
    return items.map(toWordSummary);
  },

  /**
   * Create a new word via `POST /api/meta/object` (insert-only). The response
   * carries only the new `{id, guid}`, so we re-read the full row to return a
   * complete {@link Word}.
   */
  create: async (data: WordCreate): Promise<Word> => {
    const template = await fetchCreateTemplate('pms_word');
    const res = await tahitiRequest('/meta/object?table=pms_word&lang=fr', writeResultSchema, {
      method: 'POST',
      body: JSON.stringify(toTahitiCreateBody(data, template)),
    });
    const guid = res.data?.[0]?.guid;
    if (!guid) throw new ApiError(500, 'CREATE_FAILED', 'Create returned no guid');
    return wordsApi.get(guid);
  },

  /**
   * Field-level update via `PUT /api/meta/object?id=&guid=`. Reads the current
   * row first to capture the `value0` baseline (the diff the contract requires),
   * sends only changed fields, then re-reads to return the canonical word.
   */
  patch: async (guid: string, data: WordPatch): Promise<Word> => {
    const baseline = toWriteBaseline(await fetchRow(guid));
    const qs = `table=pms_word&id=${encodeURIComponent(baseline.numericId)}&guid=${encodeURIComponent(guid)}&lang=fr`;
    await tahitiRequest(`/meta/object?${qs}`, writeResultSchema, {
      method: 'PUT',
      body: JSON.stringify(toTahitiUpdateBody(data, baseline)),
    });
    return wordsApi.get(guid);
  },

  /** Hard-delete a word via `DELETE /api/meta/object?id=` (keyed by numeric id). */
  remove: async (guid: string): Promise<void> => {
    const baseline = toWriteBaseline(await fetchRow(guid));
    const qs = `table=pms_word&id=${encodeURIComponent(baseline.numericId)}&lang=fr`;
    await tahitiRequest(`/meta/object?${qs}`, writeResultSchema, { method: 'DELETE' });
  },
};

// ── Tags API ──────────────────────────────────────────────────────────────────
//
// `pms_tag` is a real Tahiti object, but its default `meta/list` projection
// omits `tag_category`/`frequency` — verified against devdb (2026-07), both
// must be requested explicitly via `request.fields`, and both currently come
// back null on ~100% of live rows (333 tags across 4 pages @100). The frontend
// degrades to "Non catégorisé" / frequency-less ordering until the backend
// populates them (docs/Design_ Visuel UI_UX/word-detail-rework-plan.md §4).

const TAG_PAGE_SIZE = 100;
const tahitiTagListSchema = z.array(tahitiTagSchema);

/** Validate a raw tag_category string into a {@link TagCategory}, or null ("Non catégorisé"/unset). */
function parseTagCategory(raw: string | null | undefined): TagCategory | null {
  if (raw == null) return null;
  const parsed = tagCategorySchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

function tahitiTagToWordTag(t: TahitiTag): WordTag {
  const category = parseTagCategory(t.tag_category);
  const frequency = t.frequency == null ? undefined : Number(t.frequency);
  return {
    label: t.title ? String(t.title) : '',
    ...(category !== null ? { tagCategory: category } : {}),
    ...(frequency !== undefined ? { frequency } : {}),
  };
}

/** GET one `pms_tag` `meta/list` page, requesting the category/frequency fields explicitly. */
async function fetchTagCatalogPage(page: number): Promise<TahitiTag[]> {
  const request = {
    fields: ['guid', 'id', 'title', 'frequency', 'tag_category'],
    orderBy: [
      { field: 'title', direction: 'ASC' as const },
      { field: 'id', direction: 'ASC' as const },
    ],
  };
  const sp = new URLSearchParams({
    lang: 'fr',
    basecode: 'pms_tag',
    page: String(page),
    pagesize: String(TAG_PAGE_SIZE),
    request: JSON.stringify(request),
  });
  return tahitiRequest(`/meta/list?${sp.toString()}`, tahitiTagListSchema);
}

/** Fetch every page of the pms_tag catalog. */
async function fetchTagCatalog(): Promise<WordTag[]> {
  const all: TahitiTag[] = [];
  let page = 1;
  for (;;) {
    const batch = await fetchTagCatalogPage(page);
    all.push(...batch);
    if (batch.length < TAG_PAGE_SIZE) break;
    page += 1;
  }
  return all.filter((t) => t.title).map(tahitiTagToWordTag);
}

export const tagsApi = {
  /**
   * The full pickable tag vocabulary: every `pms_tag` catalog row (carrying
   * category/frequency), unioned with any label already used on a word's CSV
   * `tags` column but not yet in the catalog (e.g. legacy free-text tags).
   * Catalog entries win on label collision since they carry richer metadata.
   */
  list: async (): Promise<WordTag[]> => {
    const [wordItems, catalog] = await Promise.all([
      metaList({ orderBy: [{ field: 'title', direction: 'ASC' }], page: 1 }),
      fetchTagCatalog(),
    ]);
    const byLabel = new Map<string, WordTag>();
    for (const tag of catalog) byLabel.set(tag.label.toLowerCase(), tag);
    for (const item of wordItems) {
      for (const tag of toWord(item).tags) {
        const key = tag.label.toLowerCase();
        if (!byLabel.has(key)) byLabel.set(key, tag);
      }
    }
    return [...byLabel.values()];
  },

  /**
   * Create a real `pms_tag` row via `POST /api/meta/object` (Point 6). Unlike
   * the pre-rework stub, this is a genuine write — NOT independently verified
   * against live devdb (only the `pms_word` create-template-overlay quirk is
   * confirmed; `pms_tag` is assumed to need the same full-template overlay).
   */
  create: async (label: string, category: TagCategory | null = null): Promise<WordTag> => {
    const template = await fetchCreateTemplate('pms_tag');
    await tahitiRequest('/meta/object?table=pms_tag&lang=fr', writeResultSchema, {
      method: 'POST',
      body: JSON.stringify(toTahitiTagCreateBody(label, category, template)),
    });
    return { label, ...(category ? { tagCategory: category } : {}) };
  },
};
